import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { paths } from "@shared-core/routing/routes";
import type { OpportunityListOptions } from "@shared-core/data/adapter";
import type * as API from "@shared-core/data/api-types";
import { RecommendationCard } from "../app/components/domain/recommendations/RecommendationCard";
import {
  EntityCombobox,
  type EntityOption,
} from "../app/components/patterns/EntityCombobox";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";

// RecommendationsPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md): the backend does filter/sort/paginate.
// The page owns filter state and refetches on change; there is no
// client-side `.filter()` or `.sort()` on the adapter response.
// Server-side sort is "priority" so urgent > recommended > consider.

type StatusTab = "open" | "done" | "dismissed";
type PriorityFilter = "all" | "urgent" | "recommended" | "consider";

const PAGE_SIZE = 100;

// The "Type" typeahead offers both granularities: a family (broad grouping) or
// a specific opportunity name, encoded as ``family:`` / ``name:`` in the option
// id and mapped to the relevant filter param.
function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function RecommendationsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") ?? undefined;
  const typeName = searchParams.get("name") ?? undefined;
  const typeFamily = searchParams.get("family") ?? undefined;

  const [actions, setActions] = useState<API.Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusTab>("open");
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>("all");
  // Remember the customer option picked in the typeahead so the trigger shows
  // the name (the URL only carries the id). Falls back to the row-derived name
  // for deep links / refresh.
  const [pickedCustomer, setPickedCustomer] = useState<EntityOption | null>(
    null,
  );

  const buildOptions = useCallback((): OpportunityListOptions => {
    const options: OpportunityListOptions = {
      status: statusFilter,
      sort: "priority",
      pageSize: PAGE_SIZE,
    };
    if (priorityFilter !== "all") options.priority = priorityFilter;
    if (customerFilter) options.customerId = customerFilter;
    if (typeName) options.opportunityName = typeName;
    if (typeFamily) options.opportunityFamily = typeFamily;
    return options;
  }, [statusFilter, priorityFilter, customerFilter, typeName, typeFamily]);

  // Refetch actions whenever filter state changes.
  useEffect(() => {
    let cancelled = false;
    async function loadActions() {
      setLoading(true);
      try {
        const response = await dataAdapter.getOpportunities(buildOptions());
        if (!cancelled) setActions(response.items);
      } catch (error) {
        console.error("Error loading actions:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadActions();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, buildOptions]);

  const refreshActions = async () => {
    const response = await dataAdapter.getOpportunities(buildOptions());
    setActions(response.items);
  };

  // Typeahead option sources: live opportunity-definition catalog (families +
  // names) for type, the customer search endpoint for customer.
  const fetchTypeOptions = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const res = await dataAdapter.getRecommendationDefinitions();
      const live = res.items.filter((d) => d.status === "live");
      const families = Array.from(new Set(live.map((d) => d.family))).sort();
      const opts: EntityOption[] = [
        ...families.map((f) => ({
          id: `family:${f}`,
          label: titleCase(f),
          hint: "Family",
        })),
        ...live
          .map((d) => ({ id: `name:${d.name}`, label: d.name, hint: "Type" }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
      const needle = q.trim().toLowerCase();
      return needle
        ? opts.filter((o) => o.label.toLowerCase().includes(needle))
        : opts;
    },
    [dataAdapter],
  );

  const fetchCustomerOptions = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const res = await dataAdapter.getCustomers({
        q: q || undefined,
        pageSize: 20,
      });
      return res.items.map((c) => ({ id: c.id, label: c.name }));
    },
    [dataAdapter],
  );

  const priorities: { id: PriorityFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "urgent", label: "Urgent" },
    { id: "recommended", label: "Recommended" },
    { id: "consider", label: "Consider" },
  ];

  // When filtering by customer, every row carries that customer in
  // ``action.customer`` — pull the name from the first row rather than
  // making a second round-trip just to resolve the header.
  const customerName = customerFilter
    ? (actions.find((a) => a.customer_id === customerFilter)?.customer?.name ??
      null)
    : null;

  const selectedType: EntityOption | null = typeName
    ? { id: `name:${typeName}`, label: typeName, hint: "Type" }
    : typeFamily
      ? {
          id: `family:${typeFamily}`,
          label: titleCase(typeFamily),
          hint: "Family",
        }
      : null;

  const selectedCustomer: EntityOption | null = customerFilter
    ? pickedCustomer && pickedCustomer.id === customerFilter
      ? pickedCustomer
      : { id: customerFilter, label: customerName ?? customerFilter }
    : null;

  const handleTypeChange = (opt: EntityOption | null) => {
    const next = new URLSearchParams(searchParams);
    next.delete("name");
    next.delete("family");
    if (opt) {
      const sep = opt.id.indexOf(":");
      const kind = opt.id.slice(0, sep);
      const value = opt.id.slice(sep + 1);
      next.set(kind === "family" ? "family" : "name", value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleCustomerChange = (opt: EntityOption | null) => {
    setPickedCustomer(opt);
    const next = new URLSearchParams(searchParams);
    if (opt) next.set("customer", opt.id);
    else next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl mb-2">
          {customerFilter
            ? `Recommendations for ${customerName ?? "…"}`
            : "Recommendations"}
        </h1>
        {customerFilter && (
          <div className="mb-4">
            <CustomerScopeChip
              customerName={customerName}
              onDismiss={handleDismissCustomerScope}
            />
          </div>
        )}

        {/* Filters */}
        <div className="space-y-4">
          {/* Search by customer (top-left, mirroring estimates / orders) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-full sm:w-72">
              <EntityCombobox
                value={selectedCustomer}
                onChange={handleCustomerChange}
                fetchOptions={fetchCustomerOptions}
                placeholder="Search by customer…"
                ariaLabel="Search by customer"
              />
            </div>
          </div>

          {/* Status Tabs */}
          <div className="flex gap-2 border-b border-border">
            {(["open", "done", "dismissed"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`
                  px-4 py-2 border-b-2 transition-colors
                  ${
                    statusFilter === tab
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }
                `}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Priority pills (left) + Type filter (right) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex gap-2 flex-wrap">
              {priorities.map((priority) => (
                <button
                  key={priority.id}
                  onClick={() => setPriorityFilter(priority.id)}
                  className={`
                    px-4 py-2 rounded-full text-sm transition-colors
                    ${
                      priorityFilter === priority.id
                        ? "bg-primary text-white"
                        : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                    }
                  `}
                >
                  {priority.label}
                </button>
              ))}
            </div>
            <div className="w-full sm:w-64 shrink-0">
              <EntityCombobox
                value={selectedType}
                onChange={handleTypeChange}
                fetchOptions={fetchTypeOptions}
                placeholder="Filter by type…"
                ariaLabel="Filter by opportunity type"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        {loading
          ? "Loading recommendations…"
          : `Showing ${actions.length} recommendations`}
      </div>

      {/* Actions List */}
      {!loading && actions.length === 0 ? (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">
            {statusFilter === "open"
              ? "No recommended actions right now"
              : `No ${statusFilter} recommendations`}
          </p>
          {statusFilter === "open" && (
            <p className="text-muted-foreground">
              We'll surface suggestions here as your customers' activity
              changes.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {actions.map((action) => (
            <RecommendationCard
              key={action.id}
              action={action}
              onCreateTicket={async () => {
                // assigned_to_user_id omitted — the backend assigns to the creator.
                const newTicket = await dataAdapter.createTicket({
                  title: action.title,
                  customer_id: action.customer_id,
                  opportunity_id: action.id,
                  action_type: action.suggested_action_type,
                  due_date: action.due_date || undefined,
                });
                await dataAdapter.updateOpportunity(action.id, {
                  status: "converted_to_task",
                  converted_to_task_id: newTicket.id,
                });
                await refreshActions();
              }}
              onNotRelevant={async () => {
                await dataAdapter.updateOpportunity(action.id, {
                  status: "dismissed",
                });
                await refreshActions();
              }}
              onViewTicket={() => navigate(paths.tickets())}
            />
          ))}
        </div>
      )}
    </div>
  );
}
